import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDBodyText,
  FDDeleteButton,
  FDHeadlineText,
  FDIcon,
  FDPaper,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import { useSnackbarContext } from 'Context/SnackbarContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Button from '@mui/material/Button';
import { metaOverrideSet } from '../metaOverrideSet';

const PathDetailsLayout = ({
  refreshCollection, showConfirmDeleteFor, setShowConfirmDeleteFor,
}) => {
  const { resource } = useMetaLayoutContext();
  const [deleting, setDeleting] = useState(false);
  const { displayErrorMessage } = useSnackbarContext();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await resource.delete();
      await refreshCollection();
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to delete the path');
    } finally {
      setDeleting(false);
      setShowConfirmDeleteFor(undefined);
    }
  };

  const isConfirmingDeleteForThisPath = showConfirmDeleteFor === resource.id();

  return (
    <FDPaper appearance={'outlined'} color={'subtle'} padding={4}>
      <FDStack direction={'column'} spacing={2}>
        <FDStack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
          <FDStack direction={'row'} spacing={1} alignItems={'center'}>
            <FDIcon
              icon={'circle'}
              color={resource.attr('color')}
              size={'sm'}
            />
            <FDTitleText color={'textSecondary'} value={resource.attr('label')} />
          </FDStack>
          {resource.meta().canDelete() && !isConfirmingDeleteForThisPath ? (
            <FDDeleteButton
              onClick={() => setShowConfirmDeleteFor(resource.id())}
              appearance={'secondary'}
              size={'md'}
            />
          ) : null}
          {resource.meta().canDelete() && isConfirmingDeleteForThisPath ? (
            <Button
              disabled={deleting}
              onClick={handleDelete}
              variant="outlined"
              color={'error'}
              size={'small'}
            >
                Confirm path deletion
            </Button>
          ) : null}
        </FDStack>
        <MetaControlField attr={'name'} />
      </FDStack>
    </FDPaper>
  );
};
PathDetailsLayout.propTypes = {
  refreshCollection: PropTypes.func.isRequired,
  showConfirmDeleteFor: PropTypes.string,
  setShowConfirmDeleteFor: PropTypes.func.isRequired,
};

const RoadmapExplorerManagePathsLayout = () => {
  const { resource } = useMetaLayoutContext();
  const [submitting, setSubmitting] = useState(false);
  const { clearModalCollection, refreshModalCollection } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [showConfirmDeleteFor, setShowConfirmDeleteFor] = useState(undefined);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await resource.submit();
    } catch (err) {
      const e = err;
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to save the path');
    } finally {
      setSubmitting(false);
      refreshPageContentCollection();
      clearModalCollection();
    }
  };

  return (
    <FDPaper color={'subtle'} appearance={'flat'} height={'100%'}>
      <FDStack direction={'column'} justifyContent={'space-between'} height={'100%'} spacing={2} padding={4} overflow={'hidden'}>
        <FDHeadlineText value={resource.attr('title')} size={'md'} />
        <FDStack direction={'column'} spacing={4}>
          <FDStack direction={'column'} spacing={2}>
            {resource.attr('paths').resources().list().length === 0 && (
            <FDBodyText value={'No paths added yet'} />
            )}
            {resource.attr('paths').resources().list().map((pathResource) => {
              return (
                <MetaResourceRenderer
                  resource={pathResource}
                  layout={PathDetailsLayout}
                  metaOverrideSet={metaOverrideSet}
                  LayoutProps={{
                    refreshCollection: refreshModalCollection,
                    showConfirmDeleteFor,
                    setShowConfirmDeleteFor,
                  }}
                />
              );
            })}
          </FDStack>
        </FDStack>
        <FDStack direction={'row'} spacing={2} justifyContent={'flex-end'}>
          <FDSecondaryActionButton size="md" onClick={() => clearModalCollection()} label="Cancel" />
          <FDPrimaryActionButton size="md" disabled={submitting} onClick={() => handleSubmit()} label="Save" />
        </FDStack>
      </FDStack>
    </FDPaper>
  );
};
RoadmapExplorerManagePathsLayout.propTypes = {};

export default RoadmapExplorerManagePathsLayout;
